import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "../powerpoint/components/App";
import ExcelApp from "../excel/ExcelApp";
import { TooltipProvider } from "@finster/ui";
import { ThemeProvider } from "../shared/ThemeContext";
import { markOfficeJsUnavailable } from "../powerpoint/services/featureFlagService";
import { bootstrapAnalytics } from "../shared/analytics/client";
import {
  ensureFactSetHostContext,
  initFactSetHostContextListener,
  isFactSetSpreadsheetHost,
} from "../shared/factset/hostContextService";
import {
  addImageFromBase64ViaIntents,
  addSlideTagsViaIntents,
  applyModificationsViaIntents,
  deleteSlidesViaIntents,
  getSelectedShapesViaIntents,
  exportPresentationAsBase64ViaIntents,
  exportSlideAsImageViaIntents,
  extractPresentationContentViaIntents,
  insertSlidesFromBase64ViaIntents,
  listSlidesViaIntents,
  getSelectedSlideRefViaIntents,
  handleBatchPptOperations,
  isFactSetIntentBridgeAvailable,
  streamSlideThumbnailsViaIntents,
} from "../powerpoint/services/factset/intentBridgeService";
import { registerTaskpaneHostServices } from "./registerHostServices";
import "../shared/globals.css";

/* global document, Office, Excel, PowerPoint, module, require, HTMLElement, window */

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

declare global {
  interface Window {
    __FINSTER_HOST_INFO__?: {
      host: string;
      hostTypeExcel: string;
      typeofExcel: string;
      typeofPowerPoint: string;
      isExcel: boolean;
      platform: string;
      officeJsAvailable: boolean;
    };
    __officeJsReady?: boolean;
    __finsterBoot?: () => void;
  }
}

type FinsterFactSetBridge = {
  version: 1;
  isAvailable: () => boolean;
  getSelectedSlideRef: typeof getSelectedSlideRefViaIntents;
  getSelectedShapes: typeof getSelectedShapesViaIntents;
  listSlides: typeof listSlidesViaIntents;
  extractPresentationContent: typeof extractPresentationContentViaIntents;
  exportSlideAsImage: typeof exportSlideAsImageViaIntents;
  exportPresentationAsBase64: typeof exportPresentationAsBase64ViaIntents;
  streamSlideThumbnails: typeof streamSlideThumbnailsViaIntents;
  insertSlidesFromBase64: typeof insertSlidesFromBase64ViaIntents;
  deleteSlides: typeof deleteSlidesViaIntents;
  addImageFromBase64: typeof addImageFromBase64ViaIntents;
  addSlideTags: typeof addSlideTagsViaIntents;
  applyModifications: typeof applyModificationsViaIntents;
  handleBatchPptOperations: typeof handleBatchPptOperations;
};

function installFinsterFactSetBridge(): void {
  if (typeof window === "undefined") return;

  const bridgeWindow = window as Window & { __finsterFactSetBridge?: FinsterFactSetBridge };

  if (bridgeWindow.__finsterFactSetBridge) return;
  bridgeWindow.__finsterFactSetBridge = {
    version: 1,
    isAvailable: isFactSetIntentBridgeAvailable,
    getSelectedSlideRef: getSelectedSlideRefViaIntents,
    getSelectedShapes: getSelectedShapesViaIntents,
    listSlides: listSlidesViaIntents,
    extractPresentationContent: extractPresentationContentViaIntents,
    exportSlideAsImage: exportSlideAsImageViaIntents,
    exportPresentationAsBase64: exportPresentationAsBase64ViaIntents,
    streamSlideThumbnails: streamSlideThumbnailsViaIntents,
    insertSlidesFromBase64: insertSlidesFromBase64ViaIntents,
    deleteSlides: deleteSlidesViaIntents,
    addImageFromBase64: addImageFromBase64ViaIntents,
    addSlideTags: addSlideTagsViaIntents,
    applyModifications: applyModificationsViaIntents,
    handleBatchPptOperations: handleBatchPptOperations,
  };
}

installFinsterFactSetBridge();
registerTaskpaneHostServices();

function renderApp(isExcel: boolean) {
  root?.render(
    <ThemeProvider>
      <TooltipProvider>{isExcel ? <ExcelApp /> : <App />}</TooltipProvider>
    </ThemeProvider>
  );
}

let booted = false;

/**
 * When embedded in a FactSet-hosted iframe without Office.js, the host
 * (PowerPoint vs. Excel) can only be determined by consulting FactSet's
 * `factset.hostContext`. We first check the passively cached value from
 * `factset.onConnected`; if missing, we explicitly raise
 * `factset.GetHostContext`. Falls back to PowerPoint if we cannot reach
 * the parent within the timeout.
 */
async function detectIsExcelViaFactSetHostContext(timeoutMs = 3000): Promise<boolean> {
  try {
    const hostContext = await ensureFactSetHostContext({ timeoutMs });
    if (hostContext?.appType === "spreadsheet") {
      return true;
    }
    if (hostContext?.appType === "presentation") {
      return false;
    }
  } catch (error) {
    console.warn("[Finster] FactSet host context lookup failed:", error);
  }
  return isFactSetSpreadsheetHost();
}

async function boot() {
  if (booted) return;
  booted = true;

  void bootstrapAnalytics();
  initFactSetHostContextListener();

  const officeJsAvailable = typeof Office !== "undefined" && typeof Office.onReady === "function";

  if (officeJsAvailable) {
    let onReadyResolved = false;

    Office.onReady((info) => {
      if (onReadyResolved) return;
      onReadyResolved = true;

      const hasHost = info.host !== null && info.host !== undefined;
      const isExcel = info.host === Office.HostType.Excel;

      if (!hasHost) {
        markOfficeJsUnavailable();
      }

      window.__FINSTER_HOST_INFO__ = {
        host: String(info.host),
        hostTypeExcel: String(Office.HostType.Excel),
        typeofExcel: typeof Excel,
        typeofPowerPoint: typeof PowerPoint,
        isExcel: isExcel,
        platform: hasHost ? String(info.platform) : "standalone",
        officeJsAvailable: hasHost,
      };

      console.log(`[Finster] Detection results:`, window.__FINSTER_HOST_INFO__);
      renderApp(isExcel);
    });

    // Guard against Office.onReady never firing (script loaded but no host)
    setTimeout(() => {
      if (onReadyResolved) return;
      onReadyResolved = true;
      console.warn("[Finster] Office.onReady did not fire within 10s — falling back to standalone");
      markOfficeJsUnavailable();

      detectIsExcelViaFactSetHostContext().then((isExcel) => {
        window.__FINSTER_HOST_INFO__ = {
          host: isExcel ? "factset-excel" : "none",
          hostTypeExcel: "n/a",
          typeofExcel: typeof Excel,
          typeofPowerPoint: typeof PowerPoint,
          isExcel,
          platform: "standalone",
          officeJsAvailable: false,
        };
        renderApp(isExcel);
      });
    }, 10000);
  } else {
    markOfficeJsUnavailable();

    detectIsExcelViaFactSetHostContext().then((isExcel) => {
      window.__FINSTER_HOST_INFO__ = {
        host: isExcel ? "factset-excel" : "none",
        hostTypeExcel: "n/a",
        typeofExcel: typeof Excel,
        typeofPowerPoint: typeof PowerPoint,
        isExcel,
        platform: "standalone",
        officeJsAvailable: false,
      };

      console.log(
        `[Finster] Office JS unavailable — running in standalone mode (FactSet host: ${isExcel ? "spreadsheet" : "presentation/unknown"})`,
        window.__FINSTER_HOST_INFO__
      );
      renderApp(isExcel);
    });
  }
}

if (window.__officeJsReady === true) {
  boot();
} else if (window.__officeJsReady === false) {
  window.__finsterBoot = boot;
} else {
  boot();
}

if ((module as any).hot) {
  const renderCorrectApp = () => {
    const isExcel = window.__FINSTER_HOST_INFO__?.isExcel ?? false;
    if (isExcel) {
      const NextExcelApp = require("../excel/ExcelApp").default;
      root?.render(
        <ThemeProvider>
          <TooltipProvider>
            <NextExcelApp />
          </TooltipProvider>
        </ThemeProvider>
      );
    } else {
      const NextApp = require("../powerpoint/components/App").default;
      root?.render(
        <ThemeProvider>
          <TooltipProvider>
            <NextApp />
          </TooltipProvider>
        </ThemeProvider>
      );
    }
  };

  (module as any).hot.accept("../powerpoint/components/App", renderCorrectApp);
  (module as any).hot.accept("../excel/ExcelApp", renderCorrectApp);
}
